import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import { Link, Typography } from "@mui/material"
import { ReactElement, useState } from "react"

import {
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    DNO_APPROVED,
    ENA_SYS_ADMIN,
    INSTALLER,
    INSTALLER_ADMIN,
    LCT_MANU_APPROVED,
    PUBLIC,
} from "./roles"
import AppButton from "../components/buttons/appButton"
import ImageTips from "../components/popUps/imageTips/imageTips"

export interface IQuestion {
    category: string
    question: string
    answer: ReactElement | string
    videoId?: string
    permittedRoles: string[]
}

const FONT_WEIGHT = 300

const ALL_ROLES = [
    INSTALLER,
    DNO_APPROVED,
    LCT_MANU_APPROVED,
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    ENA_SYS_ADMIN,
]

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

const GETTING_SET_UP = "Getting Set Up"
const GENERAL = "General"
const APPLICATION = "Connection Applications"
const DATABASE_EXTRACTS = "Database Extracts"
const COMMENTS = "Comments"
const COMMISSIONING = "Commissioning"
const DEVICES = "For Manufacturers"
const FOR_DNOS = "For DNOs"

const FaqImageTips = (): ReactElement => {
    const [showImageTips, setShowImageTips] = useState(false)
    return (
        <>
            <AppButton
                sx={{ mt: 0, marginTop: 2 }}
                onClick={() => setShowImageTips(true)}
                variant="outlined"
                color="primary"
                size="small"
                endIcon={<LightbulbOutlinedIcon />}
            >
                Tips
            </AppButton>
            <ImageTips
                isOpen={showImageTips}
                close={() => setShowImageTips(false)}
            />
        </>
    )
}

export const QUESTIONS: IQuestion[] = [
    {
        category: GETTING_SET_UP,
        question: "I'm an installer, how do I get started?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                As a solo installer, or you manage of a team of installers you
                can register your company on Connect Direct. By doing so, you
                can submit and manage all your domestic LCT applications from a
                single place.
                <br />
                For team managers, you will have the ability to oversee all
                applications you team submit. Team members will be able to sign
                up with their company email address and will be automatically
                linked to the company (You will be required to review and
                approve all accounts prior to them getting access though!).
                <br />
                <br />
                Please follow the steps below to get started:
                <br />
                Step 1: Create an account and sign-in
                <br />
                Step 2: Select the ‘Register Company’ option in the set-up
                screen
                <br />
                Step 3: We will guide you through the steps to set-up your
                company and make you the admin
                <br />
                Step 4:From there, any team members who sign-up with your
                company email address will be automatically added to your
                company
                <br />
                <br />
                A video guide will available in January 2025
                <br />
            </Typography>
        ),
        permittedRoles: [PUBLIC],
        videoId: "yxFc5zm1WaM",
    },
    {
        category: GETTING_SET_UP,
        question:
            "My company has registered with Connect Direct, how do I sign up? (Installer / Manufacturer)",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Once your company has been registered on the Connect Direct
                Platform by the company administrator, please follow the steps
                outlined in the following video to learn how to create an
                account. You will need to use your company email address to sign
                up as this will automatically link you to your company user
                pool, from there your company admin will need to approve your
                account.
                <br />
                Not all companies have a company email address. In such cases,
                team members will need to be invited by the company admin to
                join the team on the Connect Direct platform
                <br />
                <br />
                A video guide will available in January 2025
                <br />
            </Typography>
        ),
        permittedRoles: [PUBLIC],
        videoId: "7MW7ccEYTYI",
    },
    {
        category: GENERAL,
        question: "What is Connect Direct?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Connect Direct is a new national platform brought to you by the
                7 UK Distribution Network Operators (DNOs) and the Energy
                Networks Association (ENA). It is used for getting approval to
                retrofit low-carbon technology (LCT) devices - both generation
                and demand - to existing residential properties. It streamlines
                and standardises the application process for all DNOs across GB
                and NI. Connect Direct is also used by LCT manufacturers! They
                use it to register their inverters, EV chargers, and heat pumps,
                thereby making them available to installers to select when
                submitting an application.
                <br />
                <br />
                ENA Connect Direct standardises data flow and communication for
                installers and enhances data feedback for DNOs with improved
                speed and consistency. By working with installers who use ENA
                Connect Direct, customers can ensure their installation is safe
                and meets all required obligations.
                <br />
                <br />
                ENA Connect Direct is a major step forward for the UK’s Energy
                System, providing a more consistent, efficient, and accurate
                process across the UK. It helps move the process from a
                centralised one-way power network to a bidirectional smart and
                flexible grid, providing DNOs increased visibility of network
                design and operation.
            </Typography>
        ),
        permittedRoles: [PUBLIC],
    },
    {
        category: GETTING_SET_UP,
        question:
            "I'm a software provider for installers, how do I get started?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You can use our enterprise APIs to programmatically submit
                applications to DNOs, and receive updates. We have open-api
                stand documentation and a sandbox environment to help you build
                out your integration. Please follow the steps below to get
                started:
                <br />
                <br />
                Step 1: Create an account and sign-in
                <br />
                Step 2: Pick ‘Software Provider’ during the set-up process
                <br />
                Step 3: We will give you access to our API documentation there
                and then
                <br />
                Step 4: We will provide access to our support portal where you
                can raise a ticket requesting API keys for our production and
                sandbox environments
                <br />
                Step 5: We will create your keys and issue them to you
                <br />
            </Typography>
        ),
        permittedRoles: [PUBLIC],
    },
    {
        category: GETTING_SET_UP,
        question: "I'm a manufacturer, how do I get started?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You can register your company on Connect Direct. This allows you
                to register your devices and make them available to installers
                to select when submitting an application. Your team members will
                be able to sign up with their company email address and will be
                automatically linked to your company (you will be required to
                review and approve all accounts prior to them getting access
                though!) Please follow the steps advised below to get started:
                <br />
                <br />
                Step 1: Create an account and sign-in
                <br />
                Step 2: Select the ‘Manufacturer option in the set-up screen
                <br />
                Step 3: We will guide you through the steps to set-up your
                company and make you the admin
                <br />
                Step 4:From there, any team members who sign-up with your
                company email address will be automatically added to your
                company
                <br />
                <br />
                Please note that if you have existing devices already registered
                on the ENA device registers, there is an additional verification
                step to protect your existing data. All devices that were
                registered with ENA prior to Connect Direct&apos;s launch in May
                2024 have been migrated to the Connect Direct platform.
            </Typography>
        ),
        permittedRoles: [PUBLIC],
    },
    {
        category: GENERAL,
        question: "How do I reset my MFA?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Please contact our support team via the{" "}
                <Link href={CONFIG.SN_PORTAL_URL} target="_blank">
                    Connect Direct Support Portal
                </Link>{" "}
                and our support team will assist you in resetting your MFA. If
                you are unable to access the support portal, please contact us
                at ena-connect-direct@baringa.com
            </Typography>
        ),
        permittedRoles: ALL_ROLES,
    },
    {
        category: GENERAL,
        question: "How do I use the search panel?",
        answer: "The search panel is a great way to get where you need to go quickly. Click the search bar or press ⌘K or CTRL+K to launch",
        permittedRoles: [PUBLIC],
    },
    {
        category: GENERAL,
        question: "How do I get help?",
        answer: "We have a support team ready help you with any queries or issues. To get in touch, please log in and raise a ticket via the Support Portal",
        permittedRoles: [PUBLIC],
    },
    {
        category: GENERAL,
        question: "How do I raise a support ticket",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You can raise a ticket here via our{" "}
                <Link href={CONFIG.SN_PORTAL_URL} target="_blank">
                    Connect Direct Support Portal
                </Link>
                <br />
                <br />
                For more information on how to raise a ticket, please refer to
                our video guide below
            </Typography>
        ),
        permittedRoles: ALL_ROLES,
        videoId: "-jEtx4CQT40",
    },
    {
        category: GENERAL,
        question: "I cannot see an installer company in the company search",
        answer: "Please note, only installers who opt to set up as a company on Connect Direct are listed here. Those that set up as solo installers do not appear on the company search (the ability to register as a solo installer was removed Dec 2024)",
        permittedRoles: [DNO_APPROVED, ENA_SYS_ADMIN],
    },
    {
        category: GENERAL,
        question: "Company details are missing from the company search",
        answer: "If you believe that company details are missing from the company search, please contact our support team via the Connect Direct Support Portal",
        permittedRoles: [DNO_APPROVED, ENA_SYS_ADMIN],
    },
    {
        category: GENERAL,
        question: "How do I change my company contact details?",
        answer: "Please raise a support ticket and we will make this change for you!",
        permittedRoles: [LCT_MANU_APPROVED, INSTALLER_ADMIN],
    },
    {
        category: FOR_DNOS,
        question:
            "How can I update DNO thresholds and configuration on the platform?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                To update your platform configuration such as your thresholds or
                email templates, please raise a ticket requesting the change.
                The support team will require written sign-off from your
                nominated head of connections representative in order to action
                the change. Once the ticket is created, our support team will
                send out this request. You can speed up the process by chasing
                this prior to raising the ticket! Once approved, our support
                team will make the required changes for you!
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: FOR_DNOS,
        question: "Company Search",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                This function allows you to search for any installer or
                manufacturer that has registered their company with Connect
                Direct
                <br />
            </Typography>
        ),
        videoId: "XMoymFymoYI",
        permittedRoles: [DNO_APPROVED, ENA_SYS_ADMIN],
    },
    {
        category: FOR_DNOS,
        question: "Workflow Assignment",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                You now can assign applications to members of your team. This
                allows you to manage workflows and assign applications to
                different users. You can also give each application a generic
                assignment status to indicate you have processed / resolved the
                application in your existing systems
                <br />
            </Typography>
        ),
        videoId: "srPdFUyhyF8",
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "Who can submit an LCT application?",
        answer: "Anyone who is competent to install an electric vehicle charger, heat pump or domestic generation asset can submit an application, including instances where the installer is working on behalf of the customer at that property. Ultimately, it is the responsibility of the connecting customer to ensure that the correct data is submitted",
        permittedRoles: [PUBLIC],
    },
    {
        category: APPLICATION,
        question: "When do I need to make an application?",
        answer: "An application must be submitted every time an installer or customer wishes to install a Solar PV system, Battery, Electric Vehicle Charge Point or Heat Pump on an existing property. All Connect Direct applications must be made pre-installation. Connect Direct cannot be used for speculative applications",
        permittedRoles: [PUBLIC],
    },
    {
        category: APPLICATION,
        question: "How do I submit an application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Submitting applications through Connect Direct is very easy,
                please follow the steps advised in the video below
                <br />
                <br />
                We have added an MPAN lookup, meaning you will no longer be
                required to enter an MPAN when filling out an application. We
                will use address details to determine the MPAN for you! In the
                case where the MPAN cannot be found, you will be asked to
                manuaully input the value once the application has been assessed
                <br />
            </Typography>
        ),
        permittedRoles: [PUBLIC],
        videoId: "FMK9ThUmtgI",
    },
    {
        category: APPLICATION,
        question: "How do I view my applications?",
        answer: "To view all the applications you have submitted as an installer, log into Connect Direct. Once logged in, click on the 'My Applications' tab to view all the applications you have submitted",
        permittedRoles: [PUBLIC],
        videoId: "E8LeQ1lnyVE",
    },
    {
        category: APPLICATION,
        question: "Can I amend an application post submission?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Yes, you can now amend applications after submitting them.
                However, applications with the following statuses cannot be
                amended:
                <br />
                <br />- Escalated & (Re) Escalated to DNO
                <br />- Awaiting assessment/ image scanning under assessment
                <br />- Commissioning approved/ update required/ submitted
                <br />- Export MPAN submitted
                <br />
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How do I assess an amended application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The application amendment will be reassessed under the same
                application ID and either be (Re) Auto-Approved or (Re)
                Escalated to DNO. If it is (Re) Escalated, it will be added to
                your queue to assess. (Re) Escalated applications are assessed
                in the same way first-time applications are.
                <br />
                <br />
                The installer has the ability to submit an amendment to an
                application. However, applications with the following statuses
                cannot be amended:
                <br />
                <br />- Escalated & (Re) Escalated to DNO
                <br />- Awaiting assessment/ image scanning under assessment
                <br />- Commissioning approved/ update required/ submitted
                <br />- Export MPAN submitted
                <br />
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "How do I create a draft application?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Creating a draft application through Connect Direct is very
                easy, please follow the steps advised below:
                <br />
                <br />- A draft is automatically created after completing the
                first page of basic details and clicking Next
                <br />- Your draft is saved every 15 seconds and whenever you
                click Next. A draft ID will be visible.
                <br />- All saved drafts can be found in the My Drafts page,
                where you can sort and filter them.
                <br />- Drafts are automatically deleted after 6 months. One
                month before deletion, the draft will be flagged. Drafts can
                also be manually deleted using the bin icon.
                <br />- To continue a draft, click on it to reopen and edit the
                application. Once submitted, the draft is removed
                <br />
            </Typography>
        ),
        permittedRoles: [INSTALLER],
        videoId: "iZLibR0fnqU",
    },
    {
        category: APPLICATION,
        question: "How do I handle multiple LCTs at a property?",
        answer: "You should raise a single application referencing all the LCTs you wish to install. These should not be raised as separate applications",
        permittedRoles: [PUBLIC],
    },
    {
        category: APPLICATION,
        question: "What networks are supported by Connect Direct?",
        answer: "All GB and NI Distribution Network Operators (DNOs) are supported by Connect Direct, as well as several Independent Distribution Network Operators (IDNOs)",
        permittedRoles: [PUBLIC],
    },
    {
        category: APPLICATION,
        question: "How do I calculate Property's Max Demand?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The Maximum Demand of a circuit, property, section of network,
                or network that considers that all loads associated with that
                network will not be drawn at the same time. There is guidance in
                the IET Code of Practice for EV Charging Equipment Installation
                on supply adequacy (Maximum Demand) assessment. This information
                is critical to understanding if the EV charge point or Heat Pump
                can be supported by the network. There are different approaches
                to determining a site&apos;s MD, including:
                <br />
                <br />
                Use existing information - this is the best approach when there
                is available data on the specific loads in the house. However,
                this may not always be the case.
                <br />
                <br />
                Determine loads installed - this approach takes diversity into
                consideration where appropriate. For this purpose there are
                different guidelines to assist installers, including:
                <br />
                <br />- Electrical installation design guide
                <br />
                <br />- calculations for electricians and designers
                <br />
                <br />- On-site guide BS 7671:2018
                <br />
                <br />- IET wiring regulations, 18th Edition
                <br />
                <br />
                Please contact the IET if you have further questions on the
                guidance. Some installers chose to post their customers an
                internet-connected monitoring device which connects to the
                customer&apos;s internet router and clamps a split Current
                Transformer (CT) around the supply meter tail. Data is then
                remotely collected over two weeks at ten minute intervals,
                before being analysed to arrive at an accurate maximum demand
                without the need for a site visit
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is a cut-out?",
        answer: (
            <>
                <Typography fontWeight={FONT_WEIGHT}>
                    A cut-out is a piece of electrical equipment that links the
                    network operator&apos;s electricity cable and the internal
                    wires in the property. To identify what cut-out the property
                    has and its associated rating, you will first need to
                    identify it and potentially speak to your local network
                    operator. Certain older cut-outs are inadequate and must be
                    upgraded by the network operator. Please see our Cut-out
                    rating guidance for more information,{" "}
                    <Link
                        href="https://www.energynetworks.org/assets/images/Resource%20library/LCT_Cut-Out%20Rating%20Guidance%20to%20EV-HP%20Installers%20v1.1.docx.pdf"
                        target="_blank"
                    >
                        click here
                    </Link>
                    . <br />
                    <br />
                    It is mandatory to provide a cut-out photo as part of the
                    Connect Direct application process. Click on the tips button
                    below for guidance on taking a good cut-out image
                </Typography>
                <FaqImageTips />
            </>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How do I identify issues with the property's cut-out?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                There is guidance available from the National Association of
                Professional Inspectors and Testers (NAPIT) on identifying
                issues with distribution network equipment. Installers sometimes
                ask for photographs prior to site visit to save resource. One
                should also read the aforementioned ENA Guidance document
                <br />
                <br />
                <Link
                    href="https://napit.org.uk/pdf/guidance-on-reporting-issues-with-distribution-businesses-service-equipment.pdf"
                    target="_blank"
                >
                    Guidance on reporting issues with Distribution Business’
                    service equipment
                </Link>
                <br />
                <br />
                <Link
                    href="https://www.napit.org.uk/pdf/supplementary-guidance-distribution-businesses-emergency-codes-explained.pdf"
                    target="_blank"
                >
                    Supplementary Guidance on Distribution Business Emergency
                    Codes
                </Link>
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is an MPAN and where can I find it?",
        answer: "The MPAN (Meter Point Administration Number) can be found on a copy of the electricity bill for that supply",
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What is an import or load limiting device?",
        answer: "An import or load limiting device is a piece of hardware and/or software that limits the demand (i.e. the amount of current drawn) by a premises. In the case of an EV charge point/Heat Pump, this can be thought of as an EV/HP curtailment scheme. This must be factored into calculations when populating the EV/HP application form. It is the responsibility of the installer to ensure that that the Maximum Demand(s) stated are correct, factoring in the effects of any load limiting devices or EV/HP curtailment schemes. The load limiting device must also “fail safe”, i.e. when it is not operating or has failed, it must not result in the limitations of the connection being exceeded",
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "What happens when my application is escalated?",
        answer: "We will pass the application over to the DNO for you. The DNO's connections team will evaluate your applications and will respond with a decision on your application. You will be notified of the outcome via Connect Direct",
        permittedRoles: [INSTALLER],
    },
    {
        category: APPLICATION,
        question: "How do I handle hybrid inverters?",
        answer: "The Connect Direct asset register has a number of hybrid inverters. If you are raising an application for one of these, first try to find the device in the register ('Live Device Register' on the navigation bar). Once located, note the category of the device. In the case of a solar a battery hybrid inverter, if the inverter is listed as type PV, then you should raise an application for a solar PV system. If the inverter is listed as type energy storage system, then you should raise an application for a battery system",
        permittedRoles: [INSTALLER, DNO_APPROVED],
    },
    {
        category: APPLICATION,
        question: "How does Connect Direct assess applications?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                The Connect Direct system has been designed to simplify the
                application process for installers. It uses a data-driven
                decision engine to assess applications. The checks in include
                ensuring:
                <br />
                <br />
                - There are no safety concerns with the cut-out, cut-out fuse,
                or other equipment.
                <br />- There is no uncertainty over the supply capacity or the
                adequacy of the supply.
                <br />- The property is not on a looped supply
                <br />- The connection does not require an upgrade
                <br />- The max demand of the property is within the supply
                capacity and DNO-defined thresholds for approval
                <br />- The equipment is compliant
                <br />- The equipment is within DNO-defined thresholds
                <br />
                <br />
                If all checks pass, the application will be auto-approved in
                real-time for you. If not, the application will be escalated to
                the DNO for review. Any updates will be communicated to you via
                Connect Direct.
                <br />
                <br />
                Each DNO configures the threshold levels within which they are
                happy to auto-approve applications. These may vary
                geographically
            </Typography>
        ),
        permittedRoles: [PUBLIC],
    },
    {
        category: APPLICATION,
        question: "How do I view applications requiring my attention as a DNO?",
        answer: "To view all applications including those that have been escalated, please follow the steps advised in the video below",
        permittedRoles: [DNO_APPROVED],
        videoId: "Tch1Hj6LWSA",
    },
    {
        category: APPLICATION,
        question:
            "Some applications have a looped supply assessment criteria, others do not. Why?",
        answer: "For generation applications below a certain installed capacity, the looped supply check is not required. In these cases, you will not see the looped supply in the assessment criteria. You are able to specify this threshold in the platform configuration. If you would like to change this threshold, please raise a ticket with our support team",
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: DEVICES,
        question:
            "I am a manufacturer who has previously registered devices, how do I access them?",
        answer: "We have migrated your existing devices into Connect Direct from both the Type Test Register and the ENA Heat Pump and EV Charge Point Registers. To get access and start administering these devices, please register your company on Connect Direct (scroll up to learn how to do this!)",
        permittedRoles: [PUBLIC],
    },
    {
        category: DEVICES,
        question: "Does this replace the Type Test Register",
        answer: "Yes! The new Connect Direct platform replaces the Type Test Register. All devices previously registered on the Type Test Register have been migrated to Connect Direct. The existing site is no longer accepting new registrations",
        permittedRoles: [PUBLIC],
    },
    {
        category: DEVICES,
        question: "How do I publish a device as a manufacturer?",
        answer: "To publish a device, please follow the steps advised in the video below",
        permittedRoles: [PUBLIC],
        videoId: "xnLziz6tuzE",
    },
    {
        category: DEVICES,
        question: "What is the difference between a Version and Amendments?",
        answer: "Manufacturers use amendments to change a non-compliant device to correct an error or omission in the original submission. Versions should be used to create a new version of the device following any changes to hardware or software",
        permittedRoles: [LCT_MANU_APPROVED, DNO_APPROVED],
    },
    {
        category: DEVICES,
        question: "What happens when an alert is raised?",
        answer: "An alert is a medium through which all DNOs, system administrators and manufacturer users can be notified of an operational issue with the device on the network. An alert circulates a system triggered email to all relevant stakeholders. Only Manufacturer admins and ENA system admins can remove and alert from a device",
        permittedRoles: [LCT_MANU_APPROVED, DNO_APPROVED],
    },
    {
        category: DEVICES,
        question: "How does my device get assessed?",
        answer: "Manufacturers can submit new devices to be included in the Type Test or demand asset registers using a device registration form on Connect Direct. Manufacturers can find this form under ‘Publish Dew Device’ tab when they log in. Please fill in the required details and the registration will be sent to an ENA assessor who will validate the information and make a decision on the compliance status of the device. The assessment process can take up to four weeks",
        permittedRoles: [LCT_MANU_APPROVED],
    },
    {
        category: DEVICES,
        question: "What are alerts?",
        answer: "Alerts are a way for Manufacturers and DNOs to notify other stakeholders of any issues observed with a device in operation. When an alert is raised, a notification is sent to all DNOs and the device manufacturer, making them aware of the issue. Manufacturers can provide evidence to ENA system administrators, who can then take the alerts down once they are satisfied with all relevant measures taken by the manufacturer",
        permittedRoles: [LCT_MANU_APPROVED, DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question: "What is commissioning, and why is it needed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Commissioning in electrical systems refers to the process of
                ensuring that all components of the electrical system are
                installed, tested, and functioning properly according to the
                design specifications and requirements. This includes verifying
                the proper installation and operation of electrical equipment,
                conducting tests to ensure safety and functionality, and
                documenting the results
                <br />
                <br />
                As the deployment of low-carbon technologies increases, network
                operators must be aware of the number and location of energy
                assets within their network to maintain the safe and efficient
                operation of the electricity network
                <br />
                <br />
                Once the application approval for connecting any low-carbon
                generation technology, such as Solar PV, Battery, or
                vehicle-to-grid (V2G), is received, the installers can proceed
                with the installation of the power-generating module
                <br />
                <br />
                When complete, the installation must undergo a series of
                performance tests (based on the size of the installed device),
                and the installers must submit the results of the tests to the
                DNOs through the Commissioning process on ENA Connect Direct.
                This step is essential if the customer wants to export the power
                generated back to the grid and get paid for it under the Smart
                Export Guarantee
                <br />
            </Typography>
        ),
        permittedRoles: [INSTALLER],
        videoId: "JlZIK5kpYVg",
    },
    {
        category: COMMISSIONING,
        question: "What is commissioning, and why is it needed?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Commissioning in electrical systems refers to the process of
                ensuring that all components of the electrical system are
                installed, tested, and functioning properly according to the
                design specifications and requirements. This includes verifying
                the proper installation and operation of electrical equipment,
                conducting tests to ensure safety and functionality, and
                documenting the results
                <br />
                <br />
                As the deployment of low-carbon technologies increases, network
                operators must be aware of the number and location of energy
                assets within their network to maintain the safe and efficient
                operation of the electricity network
                <br />
                <br />
                Once you have approved the connection application of any
                low-carbon generation technology, such as Solar PV, Battery, or
                vehicle-to-grid (V2G), the installers can proceed with the
                installation of the power-generating module
                <br />
                <br />
                Once the installation is complete, the installers must submit
                the results of performance tests to the DNOs through the
                Commissioning process on ENA Connect Direct. This step is
                essential if the customer wants to export the power generated
                back to the grid and get paid for it under the Smart Export
                Guarantee
                <br />
            </Typography>
        ),
        permittedRoles: [DNO_APPROVED],
        videoId: "qJf8ZtKL8ak",
    },
    {
        category: COMMISSIONING,
        question: "What technologies require commissioning information?",
        answer: (
            <Typography fontWeight={FONT_WEIGHT}>
                Commissioning information is required for any generating
                low-carbon technology such as:
                <br />- Solar PV
                <br />- Battery
                <br />- Vehicle to Grid
                <br />- Electric Vehicle Charge Points (V2G)
                <br />
                <br />
                Heat Pumps and EV Charge point installations do not require
                commissioning information to be supplied
            </Typography>
        ),
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question:
            "Can I provide commissioning information via ENA Connect Direct?",
        answer: "Yes! Once a connection application has been approved Commissioning forms can be entered within the Connect Direct Portal web form on your 'my applications tab'. If your systems are integrated to Connect Direct via  API, Connect Direct can also receive the Commissioning forms through this pathway",
        permittedRoles: [PUBLIC],
    },
    {
        category: COMMISSIONING,
        question: "Do DNOs review my commissioning information? ",
        answer: "Yes, the application details will be passed to the DNO for you. The DNO will review these and be given the option to either accept or request an update and further details",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question:
            "How can I see commissioning information provided by the installer",
        answer: "You can view the applications with commissioning information on your 'Awaiting review' tab",
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question:
            "How do I respond to the commissioning information provided by the installer",
        answer: "An approved application with generation devices will have a 'Submit' button in the commissioning column. Once clicked, the Commissioning Web form is loaded. See the below video for a demonstration on Commissiong form submissions",
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question:
            "What if my installation has changed vs the original approval?",
        answer: "If the installation has changed vs the what you applied for, you must submit an amendment to the application prior to submitting commissioning information. You can launch the amendment form from the commissioning form. You can use the amendment form to update the application and resubmit it. It will be reassessed under the same application ID and either be (Re) Auto-Approved or (Re) Escalated to DNO. If it is (re) escalated, the DNO will be required to re-approve the application before commissioning can take place. Once approved, you can submit commissioning information as normal",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question:
            "What happens if the installation has changed vs the original application submitted?",
        answer: "If the installation has changed vs the original approved application, the installer must submit an amendment to the application prior to submitting commissioning information.  The amendment will be reassessed under the same application ID and either be (Re) Auto-Approved or (Re) Escalated to DNO. If it is (re) escalated, it will be added to your queue to assess. (Re) Escalated applications are assessed in the same way first-time applications are. Once re-approved, commissioning information can be submitted",
        permittedRoles: [DNO_APPROVED],
    },
    {
        category: COMMISSIONING,
        question: "Can I provide commissioning information via your APIs? ",
        answer: "Yes, a new API has been added which allows you to post a commissioning information. Each form is versioned and any major changes in the future will be released as part of a new version, to ensure back compatibility",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "When does an application require commissioning? ",
        answer: "Once a connection application (with a generation component) has been approved (either by Connect Direct or by the DNO), you will be able to launch our commissioning web form",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question: "What is an Export MPAN?",
        answer: "The Smart Export Guarantee (SEG) enables small-scale low-carbon generators to receive payment for electricity they export to the grid. An installation must have an export MPAN, a 13-digit reference used to identify every electricity connection point in the country, to be eligible to receive payments under a SEG export tariff",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMISSIONING,
        question:
            "Can I get an Export MPAN without the Commissioning process? ",
        answer: "No, you need to provide commissioning information in order to get your Export MPAN",
        permittedRoles: [INSTALLER],
    },
    {
        category: COMMENTS,
        question: "How to leave comments as an Installer?",
        answer: "Within the summary application page you will see the comments section, where you are able to communicate with the DNO. For more information on how to leave comments, please see the video guide below:",
        permittedRoles: [INSTALLER],
        videoId: "aSOkctU-1Eo",
    },
    {
        category: COMMENTS,
        question: "How to leave comments as a DNO?",
        answer: "Within the summary application page you will see the comments section, where you are able to communicate with the Installer. For more information on how to leave comments, please see the video guide below:",
        permittedRoles: [DNO_APPROVED],
        videoId: "KUPXFe5Vm5I",
    },
    {
        category: COMMENTS,
        question: "How to leave comments as a Manufacturer?",
        answer: "Within the summary application page you will see the comments section, where you are able to communicate with the evaluator. For more information on how to leave comments, please see the video guide below:",
        permittedRoles: [LCT_MANU_APPROVED],
        videoId: "bF2RZeSayt4",
    },
    {
        category: DATABASE_EXTRACTS,
        question: "Can I extract updates on your live device register via API?",
        answer: "Yes! We have delta update APIs to allow you to do this. Please refer to our offical API documentation to find out how to to this",
        permittedRoles: [DNO_APPROVED, INSTALLER],
    },
]
